<script setup lang="ts">
  import type {
    Client,
    ChangeStakeholderBusinessNumbersRequestPayload,
    Vendor
  } from '@gat-ship/types'
  import { testIds } from '@gat-ship/types'
  import {
    FormField,
    FormSubmit,
    InputGroupInput,
    Button,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderDescription,
    ModalHeaderTitle,
    ModalSection,
    useForm,
    z
  } from '@gat-ship/vue-components'
  import { ref } from 'vue'
  import { FieldGroup } from '@gat-ship/vue-components'
  import { useStakeholderContext } from '../../composables/useStakeholder'
  import type { ShowStakeholderUpdateOfficialBusinessNumbersParams } from '../providers/stakeholder-modals'
  import { useStakeholderMatchMessages } from '@gat-ship/stakeholder-create'
  import { text } from '@gat-ship/stakeholders'

  const isOpen = defineModel<boolean>('open', { default: false })
  const service = useStakeholderContext()

  const _stakeholder = ref<Client | Vendor | undefined>(undefined)
  const modalTitle = ref('Official Business Numbers')
  const modalDescription = ref('')

  const { buildMatchMessages } = useStakeholderMatchMessages({
    excludeStakeholder: () => _stakeholder.value
  })

  const tinMessagesAsync = buildMatchMessages(
    () => ({
      type: _stakeholder.value?.stakeholderType ?? 'client',
      name_matching: 'exact',
      tin: ''
    }),
    { exactMatchMessage: 'This tax identification number is already in use' }
  )
  const eoriMessagesAsync = buildMatchMessages(
    () => ({
      type: _stakeholder.value?.stakeholderType ?? 'client',
      name_matching: 'exact',
      eori_number: ''
    }),
    { exactMatchMessage: 'This EORI number is already in use' }
  )

  const officialBusinessNumbersSchema = z.object({
    taxIdentificationNumber: z.string().optional(),
    eoriNumber: z.string().optional()
  })
  type OfficialBusinessNumbersFormValues = z.infer<
    typeof officialBusinessNumbersSchema
  >

  const form = useForm({
    defaultValues: {
      taxIdentificationNumber: '',
      eoriNumber: ''
    } as OfficialBusinessNumbersFormValues,
    validators: {
      onChange: officialBusinessNumbersSchema
    },
    onSubmit: async ({ value }) => {
      const stakeholder = _stakeholder.value
      if (!stakeholder) {
        throw new Error('Stakeholder not found.')
      }
      const payload: ChangeStakeholderBusinessNumbersRequestPayload = {
        taxIdentificationNumber: value.taxIdentificationNumber || null,
        eoriNumber: value.eoriNumber || null
      }
      await service?.commands.changeOfficialBusinessNumbers(
        stakeholder.id,
        payload
      )
      isOpen.value = false
    }
  })

  function showModal(
    props: ShowStakeholderUpdateOfficialBusinessNumbersParams
  ) {
    _stakeholder.value = props.stakeholder
    modalTitle.value = text.groups.officialBusinessNumbers(
      props.stakeholder.stakeholderType
    ).title
    modalDescription.value = text.groups.officialBusinessNumbers(
      props.stakeholder.stakeholderType
    ).description
    form.reset({
      taxIdentificationNumber: props.stakeholder?.taxIdentificationNumber ?? '',
      eoriNumber: props.stakeholder?.eoriNumber ?? ''
    })
    isOpen.value = true
  }

  defineExpose({ showModal })
</script>

<template>
  <modal v-model:open="isOpen">
    <ModalHeader>
      <ModalHeaderTitle>
        {{ modalTitle }}
      </ModalHeaderTitle>
      <ModalHeaderDescription v-if="modalDescription">
        {{ modalDescription }}
      </ModalHeaderDescription>
    </ModalHeader>
    <ModalContent>
      <ModalSection>
        <FieldGroup>
          <FormField
            :form="form"
            name="taxIdentificationNumber"
            label="Tax Identification Number"
            hint="Enter the official tax or registration number of this company. This ensures correct invoicing and tax handling. Examples: VAT (EU), EIN (US), Org.nr (Norway), CNPJ (Brazil)."
            :messages-async="tinMessagesAsync"
          >
            <template #default="{ field }">
              <InputGroupInput
                :id="field.name"
                :model-value="field.state.value ?? ''"
                :data-testid="
                  testIds.stakeholders.forms.officialBusinessNumbers
                    .taxIdentificationNumberInput
                "
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
          <FormField
            :form="form"
            name="eoriNumber"
            label="EORI Number"
            hint="Your EORI number identifies you for customs procedures within the EU and UK. Use the full number including the country prefix (e.g., GB123456789000)."
            :messages-async="eoriMessagesAsync"
          >
            <template #default="{ field }">
              <InputGroupInput
                :id="field.name"
                :model-value="field.state.value ?? ''"
                :data-testid="
                  testIds.stakeholders.forms.officialBusinessNumbers
                    .eoriNumberInput
                "
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
        </FieldGroup>
      </ModalSection>
    </ModalContent>
    <ModalFooter>
      <FormSubmit :form="form" label="Save Changes" />
      <Button variant="ghost" @click="isOpen = false"> Cancel </Button>
    </ModalFooter>
  </modal>
</template>
